import { fromEvent, merge, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';
import { PulseEnvironment } from '../globals';

const listenerOptions: AddEventListenerOptions = {
    capture: true,
    passive: true,
};

export class DocumentVisibilityTracker {
    get visible(): boolean {
        return this.isPageVisible();
    }

    private hiddenPropName = 'hidden';
    private visibilityStatePropName = 'visibilityState';
    private visibilityEventName = 'visibilitychange';

    constructor(private env: PulseEnvironment) {
        this.configureVisibilityListener();
    }

    onVisibilityChange(): Observable<boolean> {
        const doc = this.env.win.document;
        let visibility: Observable<boolean> = new Subject();

        if (this.configureVisibilityListener()) {
            visibility = fromEvent(doc, this.visibilityEventName).pipe(
                map(() => this.isPageVisible()),
            );
        }

        if (!this.fullVisibilityAPISupport()) {
            visibility = merge(visibility, this.legacyVisibilityEvents());
        }

        return visibility.pipe(distinctUntilChanged());
    }

    /**
     * Emit when the document is about to be unloaded.
     *
     * Prefer not to use this method because it prevents
     * browsers from putting the page in the b/f cache.
     */
    legacyOnUnload(): Observable<Event> {
        const win = this.env.win;

        return merge(this.legacyPageHide(), fromEvent(win, 'beforeunload', listenerOptions));
    }

    /**
     * PageHide don't get fired when refreshing the page.
     * @returns
     */
    legacyPageHide(): Observable<Event> {
        return fromEvent(this.env.win, 'pagehide', listenerOptions);
    }

    private legacyVisibilityEvents() {
        return merge(
            this.legacyOnUnload().pipe(map(() => false)),
            fromEvent(this.env.win, 'pageshow', listenerOptions).pipe(map(() => true)),
            fromEvent(this.env.win, 'focus', listenerOptions).pipe(map(() => true)),
        ).pipe(distinctUntilChanged());
    }

    /**
     * Check if document is visible via Page Visibility API
     */
    private isPageVisible(): boolean {
        const doc: any = this.env.doc;

        if (
            typeof doc[this.visibilityStatePropName] === 'string' &&
            doc[this.visibilityStatePropName]
        ) {
            return doc[this.visibilityStatePropName] === 'visible';
        }

        if (typeof doc[this.hiddenPropName] === 'boolean') {
            return !doc[this.hiddenPropName];
        }

        return this.env.win.document.hasFocus ? this.env.win.document.hasFocus() : true;
    }

    private configureVisibilityListener(): boolean {
        const browserPrefixes = ['', 'ms', 'webkit', 'moz', 'o'];
        const doc: any = this.env.doc;

        for (const prefix of browserPrefixes) {
            const hiddenPropName = prefix ? `${prefix}Hidden` : 'hidden';
            const visibilityStatePropName = prefix ? `${prefix}VisibilityState` : 'visibilityState';
            const visibilityEventName = (prefix || '') + 'visibilitychange';
            const supported = hiddenPropName in doc || visibilityStatePropName in doc;

            if (!supported) {
                continue;
            }

            this.hiddenPropName = hiddenPropName;
            this.visibilityEventName = visibilityEventName;
            this.visibilityStatePropName = visibilityStatePropName;

            return true;
        }

        return false;
    }

    /**
     * Detects if the browser has complete support of Visibility API
     */
    private fullVisibilityAPISupport() {
        const doc = this.env.doc;
        const ua = this.env.win.navigator.userAgent;

        return (
            typeof doc.visibilityState === 'string' &&
            typeof doc.hidden === 'boolean' &&
            typeof doc.onvisibilitychange !== 'undefined' &&
            //
            // Safari doesn't fire the visibilitychange event when navigating away from a document!
            !/Safari/i.test(ua)
        );
    }
}
